import type { MouseEvent } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { isBoolean } from "lodash";
import { useForm } from "react-hook-form";

import { toastError } from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { patchDetails } from "~/v2/api/rest/account";
import type { SubscribeStepProps } from "../Subscribe.controller";
import { EligibilityCheckPresentation } from "./EligibilityCheck.pres";
import {
  getIsProfessional,
  type NyseQuestionsSchema,
  nyseQuestionsSchema,
} from "./schema";

export const EligibilityCheckConnector = ({
  account,
  details,
  onBackToPreviousStep,
  onContinueToNextStep,
}: SubscribeStepProps) => {
  const form = useForm<NyseQuestionsSchema>({
    resolver: zodResolver(nyseQuestionsSchema),
    mode: "onChange",
    defaultValues: {
      ...Object.keys(nyseQuestionsSchema.shape).reduce((acc, k) => {
        const key = k as keyof NyseQuestionsSchema;
        const value = details?.exchange_questions?.NYSE?.[key];

        if (isBoolean(value)) {
          acc[key] = value;
        }
        return acc;
      }, {} as NyseQuestionsSchema),
    },
  });

  const isProfessionalMutation = useMutationRequest(
    async (isProfessional: boolean) => {
      if (!account?.id) {
        throw new Error("Account ID is required");
      }
      return patchDetails(account.id, { is_professional: isProfessional });
    },
    {
      onSuccess: () => {
        onContinueToNextStep();
      },
      onError: (error) => {
        console.error("Failed to update is professional", error);
        const message = getErrorMessage(error);
        toastError(`Failed to update is professional: ${message}`);
      },
    },
  );

  const questionsMutation = useMutationRequest(
    async (formValues: NyseQuestionsSchema) => {
      const payload = {
        exchange_questions: {
          NYSE: {
            occupations: "",
            employer_name_address: "",
            address: "",
            name: "",
            ...formValues,
          },
        },
      };

      return patchDetails(account?.id ?? "", payload);
    },
    {
      onSuccess: (_data, values) => {
        isProfessionalMutation.mutate(getIsProfessional(values));
      },
      onError: (error) => {
        console.error("Failed to update exchange questions", error);
        const message = getErrorMessage(error);
        toastError(`Failed to update exchange questions: ${message}`);
      },
    },
  );

  const onContinueClicked = (formValues: NyseQuestionsSchema) =>
    questionsMutation.mutate(formValues);

  const onContactUsClicked = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    window.open(
      "https://alpaca.markets/support",
      "_blank",
      "noopener,noreferrer",
    );
  };

  const values = Object.keys(nyseQuestionsSchema.shape).reduce((acc, k) => {
    const key = k as keyof NyseQuestionsSchema;
    acc[key] = form.watch(key);
    return acc;
  }, {} as NyseQuestionsSchema);

  const isIncomplete = Object.values(values).some((value) => !isBoolean(value));
  const isProfessional = getIsProfessional(values);
  const isLoading =
    questionsMutation.isPending || isProfessionalMutation.isPending;

  return (
    <EligibilityCheckPresentation
      isLoading={isLoading}
      form={form}
      isProfessional={isProfessional}
      isIncomplete={isIncomplete}
      onBackClicked={onBackToPreviousStep}
      onContinueClicked={onContinueClicked}
      onContactUsClicked={onContactUsClicked}
    />
  );
};
